import React, { useState, useCallback } from "react";
import {
	Container,
	Grid,
	Typography,
	Box,
	Paper,
	CircularProgress,
	Button,
	Stack,
	Alert,
	Fade,
	Menu,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper as MuiTablePaper,
	Chip,
	IconButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const UploadPage = ({ onBack, onApplyFilters }) => {
	const [uploadedFile, setUploadedFile] = useState(null);
	const [uploadedData, setUploadedData] = useState([]);
	const [processedData, setProcessedData] = useState([]);
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [downloadAnchor, setDownloadAnchor] = useState(null);
	const [downloadHistory, setDownloadHistory] = useState([]);
	const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
	const [downloadMenuEntry, setDownloadMenuEntry] = useState(null);
	const [convertorCount, setConvertorCount] = useState(0);
	const [fileInputKey, setFileInputKey] = useState(0);
	const [processingFailed, setProcessingFailed] = useState(false);
	const [dateRange, setDateRange] = useState({
		startDate: new Date(),
		endDate: new Date(),
	});
	const [pdfGenerating, setPdfGenerating] = useState(false);
	const [pdfConflictPopup, setPdfConflictPopup] = useState(false);
	const [pdfProgress, setPdfProgress] = useState(0);
	const [pdfPopupMessage, setPdfPopupMessage] = useState("");
	const [showFilterPreview, setShowFilterPreview] = useState(false);
	const [filterPreviewData, setFilterPreviewData] = useState(null);
	const [additionalFilterOptions, setAdditionalFilterOptions] = useState({});

	// Helper functions for PDF generation (same as main page)
	const formatNumber = (num) => {
		if (num === null || num === undefined || isNaN(num)) return "0";
		return Math.round(num).toLocaleString("en-IN");
	};

	const formatGPPercentage = (num) => {
		if (num === null || num === undefined || isNaN(num)) return "0.00%";
		return `${parseFloat(num).toFixed(2)}%`;
	};

	const formatGPValue = (num) => {
		if (num === null || num === undefined || isNaN(num)) return "0.00";
		return parseFloat(num).toFixed(2);
	};

	// Calculate totals for processed data
	const calculateTotals = (data) => {
		if (!data || data.length === 0) {
			return {
				totalBuy: 0,
				totalSold: 0,
				subCategory: "Uploaded Products",
			};
		}

		const totals = data.reduce(
			(acc, product) => {
				const troco = product.troco_ls || {};
				acc.totalBuy += parseInt(troco.t_buy || 0);
				acc.totalSold += parseInt(troco.t_sale || 0);
				return acc;
			},
			{ totalBuy: 0, totalSold: 0 }
		);

		return {
			...totals,
			subCategory: "Uploaded Products",
		};
	};

	// Calculate ST percentage
	const calculateST = (totals) => {
		if (!totals || totals.totalBuy === 0) return 0;
		return Math.round((totals.totalSold / totals.totalBuy) * 100);
	};

	// Filter labels (same as main page)
	const filterLabels = {
		brand: "Brand",
		subBrand: "Sub Brand",
		department: "Department",
		subCategory: "Sub Category",
		style: "Style",
		color: "Color",
		sleeve: "Sleeve",
		division: "Division",
		season: "Season",
		fashionability: "Fashionability",
		occasion: "Occasion",
		palette: "Palette",
		fitType: "Fit Type",
		channel: "Channel",
		businessChannel: "Business Channel",
		subChannel: "Sub Channel",
		store: "Store",
		city: "City",
		state: "State",
		region: "Region",
		tier: "Tier",
	};

	// Function to extract unique values from uploaded CSV and show filter preview
	const handleApplyToFilter = async () => {
		if (!uploadedData || uploadedData.length === 0) {
			setError("No CSV data available to apply as filters");
			return;
		}

		setIsUploading(true);
		setError("");
		setProcessingFailed(false); // Reset processing failed state

		try {
			// Extract keys from uploaded data
			const keys = uploadedData.map((row) =>
				[row.brand || "", row.department || "", row.style || "", row.color || "", row["pallete type"] || ""]
					.map((s) => (s || "").trim())
					.join("-")
			);

			// Fetch additional filter options based on the keys
			const filterOptionsResponse = await pythonVmInstance.post("/st_pictorial/filter-options/by-keys", {
				keys,
			});

			if (filterOptionsResponse.data && filterOptionsResponse.data.filter_options) {
				console.log("Additional filter options fetched:", filterOptionsResponse.data.filter_options);
				// Store the additional filter options
				setAdditionalFilterOptions(filterOptionsResponse.data.filter_options);
			}

			// Extract unique values from each column
			const uniqueValues = {};

			// Get all column names from the first row
			const columns = Object.keys(uploadedData[0] || {});

			columns.forEach((column) => {
				const values = [...new Set(uploadedData.map((row) => row[column]).filter((val) => val && val.trim()))];
				if (values.length > 0) {
					uniqueValues[column] = values;
				}
			});

			// Map CSV column names to filter names
			const columnToFilterMap = {
				brand: "brand",
				department: "department",
				style: "style",
				color: "color",
				"pallete type": "palette",
				palette: "palette",
				subBrand: "subBrand",
				subCategory: "subCategory",
				sleeve: "sleeve",
				division: "division",
				season: "season",
				fashionability: "fashionability",
				occasion: "occasion",
				fitType: "fitType",
				"fit type": "fitType",
				channel: "channel",
				businessChannel: "businessChannel",
				subChannel: "subChannel",
				store: "store",
				city: "city",
				state: "state",
				region: "region",
				tier: "tier",
			};

			// Create filters object
			const filters = {
				start_date: format(dateRange.startDate, "yyyy-MM-dd"),
				end_date: format(dateRange.endDate, "yyyy-MM-dd"),
			};

			// Add mapped filters from CSV data
			Object.entries(uniqueValues).forEach(([column, values]) => {
				const filterKey = columnToFilterMap[column];
				if (filterKey && values.length > 0) {
					filters[filterKey] = values.join(",");
				}
			});

			// Add additional filter options from database if available
			if (filterOptionsResponse.data && filterOptionsResponse.data.filter_options) {
				Object.entries(filterOptionsResponse.data.filter_options).forEach(([filterKey, values]) => {
					if (values && values.length > 0) {
						// Map database filter keys to API filter keys
						const apiFilterKey =
							filterKey === "subBrands"
								? "subBrand"
								: filterKey === "subCategories"
								? "subCategory"
								: filterKey === "sleeves"
								? "sleeve"
								: filterKey === "seasons"
								? "season"
								: filterKey === "divisions"
								? "division"
								: filterKey === "fashionability"
								? "fashionability"
								: filterKey === "occasions"
								? "occasion"
								: filterKey;

						// Only add if not already present from CSV data
						if (!filters[apiFilterKey]) {
							filters[apiFilterKey] = values.join(",");
						}
					}
				});
			}

			// Store the filter data and show preview
			setFilterPreviewData({ filters, dateRange, uniqueValues });
			setShowFilterPreview(true);
			setSuccess("Filter preview generated with additional database options. Please review and confirm.");
			// Reset file input to allow uploading the same file again
			setFileInputKey((prevKey) => prevKey + 1);
		} catch (error) {
			console.error("Error generating filter preview:", error);
			setError("Failed to generate filter preview: " + error.message);
			setProcessingFailed(true); // Mark as failed
			setSuccess(""); // Clear success message
		} finally {
			setIsUploading(false);
		}
	};

	// Function to handle routing to main page after filter confirmation
	const handleRouteToMainPage = () => {
		if (!filterPreviewData) {
			setError("No filter data available");
			return;
		}

		try {
			// Call the callback to set filters and navigate to main page
			if (onApplyFilters) {
				onApplyFilters(filterPreviewData.filters, filterPreviewData.dateRange);
			}

			setSuccess("Filters set successfully! Redirecting to main page...");
		} catch (error) {
			console.error("Error setting filters:", error);
			setError("Failed to set filters: " + error.message);
		}
	};

	// Function to cancel filter preview
	const handleCancelFilterPreview = () => {
		setShowFilterPreview(false);
		setFilterPreviewData(null);
		setSuccess("");
		setProcessingFailed(false); // Reset processing failed state
		// Reset file input to allow uploading the same file again
		setFileInputKey((prevKey) => prevKey + 1);
	};

	// Add header function (same as main page)
	const addHeader = (pdf, { currentDateTime, dateRange, totals, calculateST, formatNumber, pageWidth, filters }) => {
		// Add black rectangle at the top
		pdf.setFillColor(0, 0, 0);
		pdf.rect(0, 0, pageWidth, 15, "F");

		// Add title in white
		pdf.setTextColor(255, 255, 255);
		pdf.setFont("helvetica", "bold");
		pdf.setFontSize(24);
		pdf.text("SELL THROUGH PICTORIAL", pageWidth / 2, 11, {
			align: "center",
			renderingMode: "fill",
		});

		// Reset text color to black for rest of the content
		pdf.setTextColor(0, 0, 0);
		pdf.setFontSize(11);
		pdf.setFont("helvetica", "normal");

		// Add header content in two columns
		const leftMargin = 15;
		const rightMargin = pageWidth - 80;
		const startY = 25;
		const lineHeight = 7;

		// Left column
		pdf.text(`Date: ${format(currentDateTime, "dd-MM-yyyy HH:mm:ss")}`, leftMargin, startY);
		pdf.text(
			`Period: ${format(dateRange.startDate, "dd-MM-yyyy")} - ${format(dateRange.endDate, "dd-MM-yyyy")}`,
			leftMargin,
			startY + lineHeight
		);
		pdf.text(
			`Sub Category: ${totals.subCategory === "All" ? "All Sub Categories" : totals.subCategory}`,
			leftMargin,
			startY + lineHeight * 2
		);

		// Right column
		pdf.text(`Total Buy: ${formatNumber(totals.totalBuy)}`, rightMargin, startY);
		pdf.text(`Total Sold: ${formatNumber(totals.totalSold)}`, rightMargin, startY + lineHeight);
		pdf.text(`ST%: ${calculateST}%`, rightMargin, startY + lineHeight * 2);

		// Print selected filters below header (for upload page, show that it's from CSV upload)
		let filterY = startY + lineHeight * 4;
		pdf.text(`Source: CSV Upload (${processedData.length} products)`, leftMargin, filterY);
	};

	// Helper to get current date range (for demo, use today)
	const getCurrentDuration = () => {
		const now = new Date();
		const start = new Date(now.getFullYear(), now.getMonth(), 1);
		const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
		return {
			start,
			end,
		};
	};
	const formatDuration = (start, end) => {
		return `${format(start, "MMM dd, yyyy (hh:mm a)")} -\n${format(end, "MMM dd, yyyy (hh:mm a)")}`;
	};

	// Date change handlers
	const handleStartDateChange = (newDate) => {
		setDateRange((prev) => ({
			...prev,
			startDate: newDate,
		}));
	};

	const handleEndDateChange = (newDate) => {
		setDateRange((prev) => ({
			...prev,
			endDate: newDate,
		}));
	};

	// File upload handlers
	const handleFileUpload = (event) => {
		const file = event.target.files[0];
		if (file) {
			// Check if file is CSV
			if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
				setError("Please upload a CSV file only.");
				return;
			}

			setUploadedFile(file);
			parseCSVFile(file);
		}
	};

	const parseCSVFile = (file) => {
		setIsUploading(true);
		setError("");
		setSuccess("");

		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const csvContent = e.target.result;
				const lines = csvContent.split("\n");

				if (lines.length < 2) {
					setError("CSV file must contain at least a header row and one data row.");
					setIsUploading(false);
					return;
				}

				// Parse headers
				const headers = lines[0].split(",").map((header) => header.trim().replace(/"/g, ""));

				// Parse data rows
				const data = lines
					.slice(1)
					.filter((line) => line.trim())
					.map((line) => {
						const values = line.split(",").map((value) => value.trim().replace(/"/g, ""));
						const row = {};
						headers.forEach((header, index) => {
							row[header] = values[index] || "";
						});
						return row;
					});

				setUploadedData(data);
				console.log("Parsed CSV data:", data);

				// Don't automatically process the data - wait for submit button
				setSuccess('CSV file uploaded successfully! Click "Submit & Process Data" to continue.');
			} catch (error) {
				console.error("Error parsing CSV:", error);
				setError("Error parsing CSV file. Please check the file format.");
			} finally {
				setIsUploading(false);
			}
		};

		reader.onerror = () => {
			setError("Error reading file.");
			setIsUploading(false);
		};

		reader.readAsText(file);
	};

	const processUploadedData = async (data) => {
		setError("");
		setSuccess("Processing uploaded data...");
		setIsUploading(true);
		setProcessingFailed(false); // Reset processing failed state

		try {
			// Extract keys from uploaded data - now including brand as first column
			const keys = data.map((row) =>
				[row.brand || "", row.department || "", row.style || "", row.color || "", row["pallete type"] || ""]
					.map((s) => (s || "").trim())
					.join("-")
			);

			// Use the by-keys endpoint with date range parameters
			const formattedStartDate = format(dateRange.startDate, "yyyy-MM-dd");
			const formattedEndDate = format(dateRange.endDate, "yyyy-MM-dd");

			const response = await pythonVmInstance.post("/st_pictorial/products/by-keys", {
				keys,
				start_date: formattedStartDate,
				end_date: formattedEndDate,
			});

			if (!response.data || !response.data.products) {
				throw new Error("Invalid response format from server");
			}

			const products = response.data.products;
			console.log("Total products fetched:", products.length);

			if (products.length === 0) {
				setError("No data found for the uploaded keys in the selected date range.");
				setProcessedData([]);
				setProcessingFailed(true); // Mark as failed
				setSuccess(""); // Clear success message
				return;
			}

			// Transform products to match the expected format
			const transformedProducts = products.map((product) => ({
				...product,
				troco_ls: product.troco_ls || {
					t_buy: 0,
					t_inw: 0,
					t_sale: 0,
					st: 0,
					mrp: "0",
				},
				lcogs: product.lcogs || {
					gp_percentage: "0.00%",
					gp_value: "0.00",
					oln_cont: "0%",
					offln_cont: "0%",
					sales_cont: "0%",
				},
			}));

			setProcessedData(transformedProducts);

			if (transformedProducts.length > 0) {
				setSuccess(
					`Successfully processed ${transformedProducts.length} products! Data is ready for download in the history section below.`
				);
				// Reset file input to allow uploading the same file again
				setFileInputKey((prevKey) => prevKey + 1);
				// Increment convertor count for naming
				setConvertorCount((prev) => {
					const newCount = prev + 1;
					// Name logic
					let name = "ST Pictorial - Convertor";
					if (newCount > 1) name += ` (${newCount - 1})`;
					const newHistoryEntry = {
						id: Date.now(),
						uploadTime: new Date(),
						name,
						format: "csv/pdf",
						status: "Success",
						data: transformedProducts,
						error: null,
					};
					setDownloadHistory((prevHist) => [...prevHist, newHistoryEntry]);
					return newCount;
				});
			} else {
				setError("No products found for the uploaded keys in the selected date range.");
				setProcessingFailed(true); // Mark as failed
				setSuccess(""); // Clear success message
			}
		} catch (error) {
			console.error("Error processing uploaded data:", error);
			if (error.response && error.response.status === 500) {
				setError("No data found for the selected date range. Please try changing the date range.");
			} else {
				setError(`Error processing uploaded data: ${error.message}`);
			}
			setProcessedData([]);
			setProcessingFailed(true); // Mark as failed
			setSuccess(""); // Clear success message
		} finally {
			setIsUploading(false);
		}
	};

	const handleDownloadClick = (event) => {
		setDownloadAnchor(event.currentTarget);
	};

	const handleDownloadMenuClose = () => {
		setDownloadAnchor(null);
	};

	const handleDownloadCSV = async () => {
		try {
			console.log("Starting CSV download process...");
			setIsUploading(true);

			if (!processedData || processedData.length === 0) {
				throw new Error("No data available to download");
			}

			const fileName = `sell-through-report-upload-${format(new Date(), "yyyy-MM-dd-HH-mm")}.csv`;

			// Define CSV headers for product data
			const headers = [
				"Option",
				"Brand",
				"Sub Brand",
				"Department",
				"Sub Category",
				"Style",
				"Color",
				"Sleeve",
				"Season",
				"Division",
				"Fashionability",
				"Occasion",
				"Palette Type",
				"Blend",
				"MRP",
				"COGS",
				"T.INW",
				"T.SALE",
				"ST%",
				"Online Cont%",
				"Offline Cont%",
				"GP%",
				"GP Value",
				"Sales Cont%",
				"INV",
			];

			// Convert data to CSV rows
			const csvRows = [
				headers.join(","), // Header row
				...processedData.map((product) => {
					// For product data, use the predefined mapping
					return [
						`"${product.PKEY || "N/A"}"`,
						`"${product.brand || "N/A"}"`,
						`"${product.subBrand || "N/A"}"`,
						`"${product.category || "N/A"}"`,
						`"${product.subCategory || "N/A"}"`,
						`"${product.style || "N/A"}"`,
						`"${product.color || "N/A"}"`,
						`"${product.sleeve || "N/A"}"`,
						`"${product.season || "N/A"}"`,
						`"${product.division || "N/A"}"`,
						`"${product.fashionability || "N/A"}"`,
						`"${product.occasion || "N/A"}"`,
						`"${product.paletteType || "N/A"}"`,
						`"${product.blend || "N/A"}"`,
						`"${product.troco_ls?.mrp || "N/A"}"`,
						`"${product.COGS || "N/A"}"`,
						`"${product.troco_ls?.t_inw || "0"}"`,
						`"${product.troco_ls?.t_sale || "0"}"`,
						`"${((product.troco_ls?.t_sale / product.troco_ls?.t_buy) * 100 || 0).toFixed(2)}%"`,
						`"${product.lcogs?.oln_cont || "0%"}"`,
						`"${product.lcogs?.offln_cont || "100%"}"`,
						`"${product.lcogs?.gp_percentage || "0.00%"}"`,
						`"${product.lcogs?.gp_value || "0"}"`,
						`"${product.lcogs?.sales_cont || "0%"}"`,
						`"${product.INV || ""}"`,
					].join(",");
				}),
			];

			// Create CSV content
			const csvContent = csvRows.join("\n");

			// Create and download the file
			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute("download", fileName);
			link.style.visibility = "hidden";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			console.log("CSV saved successfully");
		} catch (error) {
			console.error("Error in CSV generation:", error);
			setError(`Failed to generate CSV: ${error.message}`);
		} finally {
			setIsUploading(false);
		}
	};

	const handleDownloadPDF = async () => {
		if (pdfGenerating) {
			setPdfConflictPopup(true);
			setTimeout(() => setPdfConflictPopup(false), 3000);
			return;
		}
		try {
			console.log("Starting PDF download process...");
			setPdfGenerating(true);
			setPdfProgress(0);
			setPdfPopupMessage("");

			if (!processedData || processedData.length === 0) {
				throw new Error("No products available to download");
			}

			// Create PDF in landscape mode
			const pdf = new jsPDF({
				orientation: "landscape",
				unit: "mm",
				format: "a2", // Use A2 for even more space
			});

			const PRODUCTS_PER_ROW = 6; // Reduced from 8 to 6 for larger cards
			const ROWS_PER_PAGE = 2;
			const PRODUCTS_PER_PAGE = PRODUCTS_PER_ROW * ROWS_PER_PAGE;
			const totalPages = Math.ceil(processedData.length / PRODUCTS_PER_PAGE);

			// Calculate totals for header
			const totals = calculateTotals(processedData);
			const stPercentage = calculateST(totals);
			const currentDateTime = new Date();

			// Create small popup notification on the right side
			const popupNotification = document.createElement("div");
			popupNotification.style.cssText = `
				position: fixed;
				top: 20px;
				right: 20px;
				width: 300px;
				background: white;
				border-radius: 12px;
				box-shadow: 0 4px 20px rgba(0,0,0,0.15);
				z-index: 9999;
				font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
				border: 1px solid #e0e0e0;
				overflow: hidden;
				animation: slideInRight 0.3s ease-out;
			`;

			// Add CSS animation for slide-in effect
			const style = document.createElement("style");
			style.textContent = `
				@keyframes slideInRight {
					from {
						transform: translateX(100%);
						opacity: 0;
					}
					to {
						transform: translateX(0);
						opacity: 1;
					}
				}
			`;
			document.head.appendChild(style);

			// Progress content
			const progressContent = document.createElement("div");
			progressContent.style.cssText = `
				padding: 20px;
				text-align: center;
			`;
			popupNotification.appendChild(progressContent);

			// Add progress bar container
			const progressBarContainer = document.createElement("div");
			progressBarContainer.style.cssText = `
				width: 100%;
				height: 4px;
				background: #e0e0e0;
				border-radius: 2px;
				overflow: hidden;
				margin-top: 10px;
			`;
			popupNotification.appendChild(progressBarContainer);

			// Add progress bar
			const progressBar = document.createElement("div");
			progressBar.style.cssText = `
				height: 100%;
				background: #000;
				width: 0%;
				transition: width 0.3s ease;
				border-radius: 2px;
			`;
			progressBarContainer.appendChild(progressBar);

			document.body.appendChild(popupNotification);

			// Create temporary container for rendering
			const tempContainer = document.createElement("div");
			tempContainer.style.cssText = `
				position: absolute;
				left: -9999px;
				width: 2400px;
				background: white;
				padding: 20px;
			`;
			document.body.appendChild(tempContainer);

			for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
				const progress = Math.round((pageNum / totalPages) * 100);
				setPdfProgress(progress);
				progressContent.innerHTML = `
					<h3 style="margin: 0 0 10px 0; color: #333; font-size: 16px;">Generating PDF</h3>
					<p style="margin: 5px 0; color: #666; font-size: 14px;">Processing page ${pageNum} of ${totalPages}</p>
					<p style="margin: 5px 0; color: #000; font-weight: 600; font-size: 14px;">${progress}% complete</p>
				`;
				progressBar.style.width = `${progress}%`;

				const startIdx = (pageNum - 1) * PRODUCTS_PER_PAGE;
				const endIdx = Math.min(startIdx + PRODUCTS_PER_PAGE, processedData.length);
				const pageProducts = processedData.slice(startIdx, endIdx);

				// Create grid for this page
				const gridContainer = document.createElement("div");
				gridContainer.style.cssText = `
					display: grid;
					grid-template-columns: repeat(${PRODUCTS_PER_ROW}, 1fr);
					gap: 20px;
					padding: 20px;
					background: white;
				`;

				// Add products to grid
				pageProducts.forEach((product) => {
					const card = createProductCard(product);
					gridContainer.appendChild(card);
				});

				tempContainer.innerHTML = "";
				tempContainer.appendChild(gridContainer);

				// Wait for images to load
				await new Promise((resolve) => {
					const images = tempContainer.getElementsByTagName("img");
					let loadedImages = 0;
					const totalImages = images.length;

					if (totalImages === 0) {
						resolve();
						return;
					}

					const imageLoaded = () => {
						loadedImages++;
						if (loadedImages === totalImages) {
							resolve();
						}
					};

					Array.from(images).forEach((img) => {
						if (img.complete) {
							imageLoaded();
						} else {
							img.onload = imageLoaded;
							img.onerror = imageLoaded;
						}
						img.crossOrigin = "anonymous";
					});
				});

				try {
					const canvas = await html2canvas(gridContainer, {
						scale: 2,
						useCORS: true,
						allowTaint: true,
						backgroundColor: "#ffffff",
						logging: true,
						width: gridContainer.offsetWidth,
						height: gridContainer.offsetHeight,
					});

					if (pageNum > 1) {
						pdf.addPage();
					}

					// Add header (same as main page)
					addHeader(pdf, {
						currentDateTime,
						dateRange,
						totals,
						calculateST: stPercentage,
						formatNumber,
						pageWidth: pdf.internal.pageSize.getWidth(),
						filters: {}, // No filters for upload page
					});

					const pageWidth = pdf.internal.pageSize.getWidth();
					const pageHeight = pdf.internal.pageSize.getHeight();
					const margin = 20;
					const availableHeight = pageHeight - 45 - margin * 2;

					const scale = Math.min((pageWidth - margin * 2) / canvas.width, availableHeight / canvas.height);

					const scaledWidth = canvas.width * scale;
					const scaledHeight = canvas.height * scale;

					// Center the content
					const x = (pageWidth - scaledWidth) / 2;
					const y = 45 + (availableHeight - scaledHeight) / 2;

					pdf.addImage(canvas.toDataURL("image/jpeg", 1.0), "JPEG", x, y, scaledWidth, scaledHeight);
				} catch (canvasError) {
					console.error("Error generating canvas for page", pageNum, canvasError);
					throw new Error(`Failed to generate PDF page ${pageNum}`);
				}
			}

			// Clean up temporary container and popup
			document.body.removeChild(tempContainer);
			document.body.removeChild(popupNotification);

			// Save the PDF
			const fileName = `sell-through-report-upload-${format(new Date(), "yyyy-MM-dd-HH-mm")}.pdf`;
			pdf.save(fileName);

			console.log("PDF saved successfully");
		} catch (error) {
			console.error("Error in PDF generation:", error);
			setError(`Failed to generate PDF: ${error.message}`);
		} finally {
			setPdfGenerating(false);
		}
	};

	// Add createProductCard function (adapted from App.js)
	function createProductCard(product) {
		const card = document.createElement("div");
		card.style.cssText = `
			width: 340px;
			background-color: #ffffff;
			display: flex;
			flex-direction: column;
			font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
			cursor: pointer;
			padding: 10px;
			box-sizing: border-box;
		`;

		// Image container
		const imageContainer = document.createElement("div");
		imageContainer.style.cssText = `
			width: 100%;
			height: 340px;
			overflow: hidden;
			margin-bottom: 12px;
		`;

		const img = document.createElement("img");
		img.src = product.image_url || product.URL || "";
		img.crossOrigin = "anonymous";
		img.style.cssText = `
			width: 100%;
			height: 100%;
			object-fit: cover;
		`;
		imageContainer.appendChild(img);

		// Format MRP and COGS without decimals
		const formatCurrency = (value) => {
			if (!value || value === "N/A") return "N/A";
			// Remove any existing commas and handle string "N/A"
			const cleanValue = value.toString().replace(/,/g, "");
			// Parse the number and round it
			const numValue = Math.round(parseFloat(cleanValue));
			// Check if it's a valid number
			if (isNaN(numValue)) return "N/A";
			// Format with commas but no decimals
			return numValue.toLocaleString("en-IN");
		};

		// Product details
		const details = document.createElement("div");
		details.style.cssText = `
			padding: 10px;
			font-size: 15px;
			line-height: 1.5;
		`;

		details.innerHTML = `
			<div style="font-weight: bold; font-size: 16px; margin-bottom: 8px;">OPTION: ${product.PKEY || "N/A"}</div>
			<div style="display: flex; gap: 32px;">
				<div style="flex: 1;">
					<div style="margin-bottom: 4px;">Season: ${product.season || product.SEASON || "N/A"}</div>
					<div style="margin-bottom: 4px;">BLEND: ${product.blend || product.BLEND || "N/A"}</div>
					<div style="margin-bottom: 4px;">MRP: ${formatCurrency(product.troco_ls?.mrp || product.MRP)}</div>
					<div style="margin-bottom: 4px;">T.INW: ${formatNumber(product.troco_ls?.t_inw || 0)}</div>
					<div style="margin-bottom: 4px;">T.SALE: ${formatNumber(product.troco_ls?.t_sale || 0)}</div>
					<div style="margin-bottom: 4px;">ST: ${((product.troco_ls?.t_sale / product.troco_ls?.t_buy) * 100 || 0).toFixed(2)}%</div>
					<div style="margin-bottom: 4px;">Online Cont: ${product.lcogs?.oln_cont || "0%"}</div>
					<div>Offline Cont: ${product.lcogs?.offln_cont || "100%"}</div>
				</div>
				<div style="flex: 1;">
					<div style="margin-bottom: 4px;">COGS: ${formatCurrency(product.COGS)}</div>
					<div style="margin-bottom: 4px;">GP%: ${formatGPPercentage(product.lcogs?.gp_percentage || "0.00%")}</div>
					<div style="margin-bottom: 4px;">GP Value: ${formatGPValue(product.lcogs?.gp_value || 0)}</div>
					<div style="margin-bottom: 4px;">Sales Cont: ${product.lcogs?.sales_cont || "0%"}</div>
					<div>INV: ${product.INV || ""}</div>
				</div>
			</div>
		`;

		card.appendChild(imageContainer);
		card.appendChild(details);

		return card;
	}

	// Update handleHistoryDownloadPDF to use the main page's PDF export logic
	const handleHistoryDownloadPDF = async (entry) => {
		if (pdfGenerating) {
			setPdfConflictPopup(true);
			setTimeout(() => setPdfConflictPopup(false), 3000);
			return;
		}

		try {
			setPdfGenerating(true);
			const { default: jsPDF } = await import("jspdf");

			const PRODUCTS_PER_ROW = 6;
			const ROWS_PER_PAGE = 2;
			const PRODUCTS_PER_PAGE = PRODUCTS_PER_ROW * ROWS_PER_PAGE;
			const productsToProcess = entry.data;

			if (!productsToProcess || productsToProcess.length === 0) return;

			const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a2" });
			const totalPages = Math.ceil(productsToProcess.length / PRODUCTS_PER_PAGE);

			// Calculate totals for header
			const totals = calculateTotals(productsToProcess);
			const stPercentage = calculateST(totals);
			const currentDateTime = new Date();

			// Create temporary container for rendering
			const tempContainer = document.createElement("div");
			tempContainer.style.cssText = `
				position: absolute;
				left: -9999px;
				width: 2400px;
				background: white;
				padding: 20px;
				z-index: 9999;
			`;
			document.body.appendChild(tempContainer);

			for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
				const startIdx = (pageNum - 1) * PRODUCTS_PER_PAGE;
				const endIdx = Math.min(startIdx + PRODUCTS_PER_PAGE, productsToProcess.length);
				const pageProducts = productsToProcess.slice(startIdx, endIdx);

				// Create grid for this page
				const gridContainer = document.createElement("div");
				gridContainer.style.cssText = `
					display: grid;
					grid-template-columns: repeat(${PRODUCTS_PER_ROW}, 1fr);
					gap: 20px;
					padding: 20px;
					background: white;
				`;

				// Add products to grid
				pageProducts.forEach((product) => {
					const card = createProductCard(product);
					gridContainer.appendChild(card);
				});

				tempContainer.innerHTML = "";
				tempContainer.appendChild(gridContainer);

				// Wait for images to load
				await new Promise((resolve) => {
					const images = tempContainer.getElementsByTagName("img");
					let loadedImages = 0;
					const totalImages = images.length;

					if (totalImages === 0) {
						resolve();
						return;
					}

					const imageLoaded = () => {
						loadedImages++;
						if (loadedImages === totalImages) {
							resolve();
						}
					};

					Array.from(images).forEach((img) => {
						if (img.complete) {
							imageLoaded();
						} else {
							img.onload = imageLoaded;
							img.onerror = imageLoaded;
						}
						img.crossOrigin = "anonymous";
					});
				});

				try {
					const canvas = await html2canvas(gridContainer, {
						scale: 2,
						useCORS: true,
						allowTaint: true,
						backgroundColor: "#ffffff",
						logging: true,
						width: gridContainer.offsetWidth,
						height: gridContainer.offsetHeight,
					});

					if (pageNum > 1) {
						pdf.addPage();
					}

					// Add header (same as main page)
					addHeader(pdf, {
						currentDateTime,
						dateRange,
						totals,
						calculateST: stPercentage,
						formatNumber,
						pageWidth: pdf.internal.pageSize.getWidth(),
						filters: {}, // No filters for upload page
					});

					const pageWidth = pdf.internal.pageSize.getWidth();
					const pageHeight = pdf.internal.pageSize.getHeight();
					const margin = 20;
					const availableHeight = pageHeight - 45 - margin * 2;

					const scale = Math.min((pageWidth - margin * 2) / canvas.width, availableHeight / canvas.height);

					const scaledWidth = canvas.width * scale;
					const scaledHeight = canvas.height * scale;

					// Center the content
					const x = (pageWidth - scaledWidth) / 2;
					const y = 45 + (availableHeight - scaledHeight) / 2;

					pdf.addImage(canvas.toDataURL("image/jpeg", 1.0), "JPEG", x, y, scaledWidth, scaledHeight);
				} catch (error) {
					console.error(`Error processing page ${pageNum}:`, error);
					throw error;
				}
			}

			// Clean up
			document.body.removeChild(tempContainer);

			const fileName = `sell-through-report-history-${entry.id}.pdf`;
			pdf.save(fileName);
			console.log("History PDF saved successfully");
		} catch (error) {
			console.error("Error in history PDF generation:", error);
			setError(`Failed to generate PDF: ${error.message}`);
		} finally {
			setPdfGenerating(false);
		}
	};

	// Add missing functions
	const downloadSampleCSV = () => {
		try {
			// Sample CSV content with brand as the first column
			const sampleData = [
				"brand,department,style,color,pallete type",
				"RARE RABBIT,T-SHIRT,REV,BLACK,PRIMARY",
				"RARE RABBIT,SHIRT,CLASSIC,BEIGE,DARK",
			];
			const csvContent = sampleData.join("\n");
			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute("download", `sample-csv-structure-${format(new Date(), "yyyy-MM-dd")}.csv`);
			link.style.visibility = "hidden";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			setSuccess("Sample CSV file downloaded successfully!");
		} catch (error) {
			console.error("Error generating sample CSV:", error);
			setError(`Failed to generate sample CSV: ${error.message}`);
		}
	};

	const clearUploadedData = () => {
		setUploadedFile(null);
		setUploadedData([]);
		setProcessedData([]);
		setError("");
		setSuccess("");
		setShowFilterPreview(false);
		setFilterPreviewData(null);
		setAdditionalFilterOptions({});
		setFileInputKey((prevKey) => prevKey + 1);
		setProcessingFailed(false);
	};

	// Helper to trigger CSV download for a history entry
	const handleHistoryDownloadCSV = (entry) => {
		try {
			if (!entry.data || entry.data.length === 0) {
				setError("No data available to download");
				return;
			}

			const fileName = `${entry.name || "processed-products"}-${format(new Date(), "yyyy-MM-dd-HH-mm")}.csv`;

			// Define CSV headers for product data
			const headers = [
				"Option",
				"Brand",
				"Sub Brand",
				"Department",
				"Sub Category",
				"Style",
				"Color",
				"Sleeve",
				"Season",
				"Division",
				"Fashionability",
				"Occasion",
				"Palette Type",
				"Blend",
				"MRP",
				"COGS",
				"T.INW",
				"T.SALE",
				"ST%",
				"Online Cont%",
				"Offline Cont%",
				"GP%",
				"GP Value",
				"Sales Cont%",
				"INV",
			];

			// Convert data to CSV rows
			const csvRows = [
				headers.join(","), // Header row
				...entry.data.map((product) => {
					// For product data, use the predefined mapping
					return [
						`"${product.PKEY || "N/A"}"`,
						`"${product.brand || "N/A"}"`,
						`"${product.subBrand || "N/A"}"`,
						`"${product.category || "N/A"}"`,
						`"${product.subCategory || "N/A"}"`,
						`"${product.style || "N/A"}"`,
						`"${product.color || "N/A"}"`,
						`"${product.sleeve || "N/A"}"`,
						`"${product.season || "N/A"}"`,
						`"${product.division || "N/A"}"`,
						`"${product.fashionability || "N/A"}"`,
						`"${product.occasion || "N/A"}"`,
						`"${product.paletteType || "N/A"}"`,
						`"${product.blend || "N/A"}"`,
						`"${product.troco_ls?.mrp || "N/A"}"`,
						`"${product.COGS || "N/A"}"`,
						`"${product.troco_ls?.t_inw || "0"}"`,
						`"${product.troco_ls?.t_sale || "0"}"`,
						`"${((product.troco_ls?.t_sale / product.troco_ls?.t_buy) * 100 || 0).toFixed(2)}%"`,
						`"${product.lcogs?.oln_cont || "0%"}"`,
						`"${product.lcogs?.offln_cont || "100%"}"`,
						`"${product.lcogs?.gp_percentage || "0.00%"}"`,
						`"${product.lcogs?.gp_value || "0"}"`,
						`"${product.lcogs?.sales_cont || "0%"}"`,
						`"${product.INV || ""}"`,
					].join(",");
				}),
			];

			// Convert data to CSV rows
			const csvContent = csvRows.join("\n");

			// Create and download the file
			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute("download", fileName);
			link.style.visibility = "hidden";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			console.log("CSV saved successfully");
		} catch (error) {
			console.error("Error in CSV generation:", error);
			setError(`Failed to generate CSV: ${error.message}`);
		}
	};

	return (
		<Container maxWidth="lg" sx={{ pt: 3 }}>
			<Paper sx={{ p: 4, backgroundColor: "#fff" }}>
				{/* Header */}
				<Box sx={{ mb: 4 }}>
					<Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }} variant="outlined">
						Back to Main Page
					</Button>
					<Typography variant="h4" sx={{ color: "#000", fontWeight: 600, mb: 1 }}>
						Sell Through Report Generator and Filter Uploader
					</Typography>
					<Typography variant="body1" sx={{ color: "#666" }}>
						Upload a CSV file and select date range to generate sell-through reports or upload a filter to the main page
					</Typography>
				</Box>

				{/* Error and Success Messages */}
				{error && (
					<Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
						{error}
					</Alert>
				)}
				{success && (
					<Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
						{success}
					</Alert>
				)}

				{/* Upload Section */}
				<Box sx={{ mb: 4, p: 3, border: "1px solid #e0e0e0", borderRadius: 2, backgroundColor: "#fafafa" }}>
					<Typography variant="h6" sx={{ mb: 3, color: "#000", fontWeight: 600 }}>
						Upload CSV File with Date Range
					</Typography>

					{/* Date Selection */}
					<Box sx={{ mb: 3 }}>
						<Typography variant="subtitle1" sx={{ mb: 2, color: "#000", fontWeight: 500 }}>
							Select Date Range
						</Typography>
						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<Grid container spacing={2} alignItems="center" justifyContent="flex-start">
								<Grid item>
									<DatePicker
										label="Start Date"
										value={dateRange.startDate}
										onChange={handleStartDateChange}
										slotProps={{ textField: { size: "small" } }}
										format="dd-MM-yyyy"
									/>
								</Grid>
								<Grid item>
									<DatePicker
										label="End Date"
										value={dateRange.endDate}
										onChange={handleEndDateChange}
										minDate={dateRange.startDate}
										slotProps={{ textField: { size: "small" } }}
										format="dd-MM-yyyy"
									/>
								</Grid>
								<Grid item>
									<Typography variant="body2" sx={{ color: "#666" }}>
										Period: {format(dateRange.startDate, "dd-MM-yyyy")} - {format(dateRange.endDate, "dd-MM-yyyy")}
									</Typography>
								</Grid>
							</Grid>
						</LocalizationProvider>
					</Box>

					{/* File Upload */}
					<Box sx={{ mb: 3 }}>
						<Typography variant="subtitle1" sx={{ mb: 2, color: "#000", fontWeight: 500 }}>
							Upload CSV File
						</Typography>
						<Grid container spacing={3} alignItems="center" justifyContent="flex-start">
							<Grid item>
								<input
									accept=".csv"
									style={{ display: "none" }}
									id="csv-file-upload"
									type="file"
									onChange={handleFileUpload}
									disabled={isUploading}
									key={fileInputKey}
								/>
								<label htmlFor="csv-file-upload">
									<Button
										variant="outlined"
										component="span"
										disabled={isUploading}
										startIcon={<UploadFileIcon />}
										size="large"
										sx={{
											borderColor: "#000",
											color: "#000",
											"&:hover": {
												borderColor: "#333",
												backgroundColor: "rgba(0, 0, 0, 0.04)",
											},
										}}>
										{uploadedFile ? "Change File" : "Upload CSV File"}
									</Button>
								</label>
							</Grid>
							<Grid item>
								<Button
									variant="outlined"
									onClick={downloadSampleCSV}
									size="large"
									startIcon={<UploadFileIcon sx={{ transform: "rotate(180deg)" }} />}
									sx={{
										borderColor: "#007bff",
										color: "#007bff",
										"&:hover": {
											borderColor: "#0056b3",
											backgroundColor: "rgba(0, 123, 255, 0.04)",
										},
									}}>
									Download Sample CSV
								</Button>
							</Grid>
						</Grid>
					</Box>

					{/* Submit Button */}
					<Box sx={{ mb: 3 }}>
						<Grid container spacing={2} alignItems="center" justifyContent="flex-start">
							<Grid item>
								<Button
									variant="contained"
									onClick={() => uploadedData.length > 0 && processUploadedData(uploadedData)}
									disabled={isUploading || uploadedData.length === 0}
									size="large"
									sx={{
										backgroundColor: "#28a745",
										"&:hover": {
											backgroundColor: "#218838",
										},
										"&:disabled": {
											backgroundColor: "#6c757d",
										},
									}}>
									{isUploading ? "Processing..." : "Submit & Process Data"}
								</Button>
							</Grid>
							<Grid item>
								{!showFilterPreview ? (
									<Button
										variant="contained"
										onClick={handleApplyToFilter}
										disabled={uploadedData.length === 0}
										size="large"
										sx={{
											backgroundColor: "#007bff",
											"&:hover": {
												backgroundColor: "#0056b3",
											},
											"&:disabled": {
												backgroundColor: "#6c757d",
											},
										}}>
										Apply to Filter
									</Button>
								) : (
									<Button
										variant="contained"
										onClick={handleRouteToMainPage}
										size="large"
										sx={{
											backgroundColor: "#28a745",
											"&:hover": {
												backgroundColor: "#218838",
											},
										}}>
										Set Filters & Go to Main Page
									</Button>
								)}
							</Grid>
							<Grid item>
								<Stack direction="row" spacing={2} justifyContent="flex-end">
									{showFilterPreview && (
										<Button
											variant="outlined"
											onClick={handleCancelFilterPreview}
											size="large"
											sx={{
												borderColor: "#6c757d",
												color: "#6c757d",
												"&:hover": {
													borderColor: "#545b62",
													backgroundColor: "rgba(108, 117, 125, 0.04)",
												},
											}}>
											Cancel
										</Button>
									)}
									{processingFailed && !showFilterPreview && (
										<Button
											variant="outlined"
											onClick={clearUploadedData}
											size="large"
											sx={{
												borderColor: "#dc3545",
												color: "#dc3545",
												"&:hover": {
													borderColor: "#c82333",
													backgroundColor: "rgba(220, 53, 69, 0.04)",
												},
											}}>
											Reset & Try Again
										</Button>
									)}
									{processedData.length > 0 && !showFilterPreview && !processingFailed && (
										<Button
											variant="outlined"
											onClick={clearUploadedData}
											size="large"
											sx={{
												borderColor: "#dc3545",
												color: "#dc3545",
												"&:hover": {
													borderColor: "#c82333",
													backgroundColor: "rgba(220, 53, 69, 0.04)",
												},
											}}>
											Clear Data
										</Button>
									)}
								</Stack>
							</Grid>
						</Grid>
					</Box>

					{/* Filter Preview */}
					{showFilterPreview && filterPreviewData && (
						<Box sx={{ mb: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1, backgroundColor: "#f8f9fa" }}>
							<Typography variant="subtitle2" sx={{ mb: 1, color: "#000", fontWeight: 600 }}>
								Filter Preview (will be set on main page):
							</Typography>
							<Grid container spacing={1}>
								{Object.entries(filterPreviewData.uniqueValues).map(([column, values]) => (
									<Grid item xs={12} sm={6} md={4} key={column}>
										<Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
											{column}: <span style={{ color: "#007bff" }}>{values.join(", ")}</span>
										</Typography>
									</Grid>
								))}
							</Grid>
							{/* Additional Filter Options from Database */}
							{Object.keys(additionalFilterOptions).length > 0 && (
								<Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e0e0e0" }}>
									<Typography variant="subtitle2" sx={{ mb: 1, color: "#000", fontWeight: 600 }}>
										Additional Filter Options (from database):
									</Typography>
									<Grid container spacing={1}>
										{Object.entries(additionalFilterOptions).map(([filterKey, values]) => (
											<Grid item xs={12} sm={6} md={4} key={filterKey}>
												<Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
													{filterLabels[filterKey] || filterKey}:{" "}
													<span style={{ color: "#28a745" }}>{values.join(", ")}</span>
												</Typography>
											</Grid>
										))}
									</Grid>
								</Box>
							)}
							<Typography variant="body2" sx={{ mt: 2, color: "#666", fontStyle: "italic" }}>
								Date Range: {format(dateRange.startDate, "dd-MM-yyyy")} - {format(dateRange.endDate, "dd-MM-yyyy")}
							</Typography>
							<Typography variant="body2" sx={{ mt: 1, color: "#666", fontStyle: "italic" }}>
								Note: Filters will be pre-selected on the main page. You can modify them before applying.
							</Typography>
						</Box>
					)}

					{/* Processing Status */}
					{isUploading && (
						<Box
							sx={{
								mt: 3,
								display: "flex",
								alignItems: "center",
								gap: 2,
								p: 3,
								backgroundColor: "#e3f2fd",
								borderRadius: 1,
							}}>
							<CircularProgress size={24} />
							<Typography variant="body1" sx={{ color: "#1976d2", fontWeight: 500 }}>
								Processing uploaded data... Please wait.
							</Typography>
						</Box>
					)}
				</Box>

				{/* Download History Section */}
				<Box sx={{ mb: 4 }}>
					<Typography variant="h6" sx={{ mb: 2, color: "#000", fontWeight: 600 }}>
						Download History
					</Typography>
					<TableContainer component={MuiTablePaper} sx={{ background: "#f8fafd" }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 600 }}>Uploaded At</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Format</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Download</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{downloadHistory.length === 0 ? (
									<TableRow>
										<TableCell colSpan={5} align="center" sx={{ color: "#888" }}>
											No downloads yet.
										</TableCell>
									</TableRow>
								) : (
									downloadHistory.map((entry) => (
										<TableRow key={entry.id}>
											<TableCell>
												{entry.uploadTime ? format(new Date(entry.uploadTime), "MMM dd, yyyy HH:mm:ss") : ""}
											</TableCell>
											<TableCell>{entry.name}</TableCell>
											<TableCell>{entry.format}</TableCell>
											<TableCell>
												{entry.status === "Processing" ? (
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<CircularProgress size={18} />
														<Typography variant="body2" sx={{ color: "#888" }}>
															Processing...
														</Typography>
													</Box>
												) : (
													<Chip
														label={entry.status}
														color={entry.status === "Success" ? "success" : "error"}
														size="small"
													/>
												)}
											</TableCell>
											<TableCell>
												<Stack direction="row" spacing={1}>
													<Button
														variant="outlined"
														size="small"
														onClick={() => handleHistoryDownloadCSV(entry)}
														disabled={entry.status !== "Success"}
														startIcon={<DownloadIcon />}
														sx={{
															borderColor: "#28a745",
															color: "#28a745",
															"&:hover": {
																borderColor: "#218838",
																backgroundColor: "rgba(40, 167, 69, 0.04)",
															},
														}}>
														CSV
													</Button>
													<Button
														variant="outlined"
														size="small"
														onClick={() => handleHistoryDownloadPDF(entry)}
														disabled={entry.status !== "Success" || pdfGenerating}
														startIcon={<DownloadIcon />}
														sx={{
															borderColor: "#dc3545",
															color: "#dc3545",
															"&:hover": {
																borderColor: "#c82333",
																backgroundColor: "rgba(220, 53, 69, 0.04)",
															},
														}}>
														{pdfGenerating ? "Generating..." : "PDF"}
													</Button>
												</Stack>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>

				{/* Instructions Section */}
				<Box sx={{ p: 3, border: "1px solid #e0e0e0", borderRadius: 2, backgroundColor: "#f8f9fa", mb: 3 }}>
					<Typography variant="h6" sx={{ mb: 2, color: "#000", fontWeight: 600 }}>
						Instructions
					</Typography>
					<Stack spacing={1}>
						<Typography variant="body2" sx={{ color: "#666" }}>
							1. Select a date range for the report period
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							2. Upload a CSV with columns: <b>brand, department, style, color, pallete type</b>
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							3. Each row should match a product key: brand-department-style-color-pallete type
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							4. Choose your action:
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", ml: 2 }}>
							• <b>"Submit & Process Data"</b>: Process the CSV and download enriched data as CSV or PDF
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", ml: 2 }}>
							• <b>"Apply to Filter"</b>: Use the CSV data as filters on the main page to view all matching products
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", ml: 4, fontSize: "0.9em" }}>
							- Click "Apply to Filter" to see a preview of filters that will be set (includes additional database
							options)
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", ml: 4, fontSize: "0.9em" }}>
							- The system will fetch additional filter options (sub brand, sub category, sleeve, season, division,
							fashionability, occasion) from the database
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", ml: 4, fontSize: "0.9em" }}>
							- Review the preview and click "Set Filters & Go to Main Page" to pre-select filters
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", ml: 4, fontSize: "0.9em" }}>
							- On the main page, you can modify filters and click "Apply Filters" when ready
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							Example header:{" "}
							<span style={{ fontFamily: "monospace", color: "#2e7d32" }}>
								brand,department,style,color,pallete type
							</span>
						</Typography>
					</Stack>
				</Box>

				{/* PDF Conflict Popup */}
				{pdfConflictPopup && (
					<div
						style={{
							position: "fixed",
							top: 150,
							right: 20,
							background: "#fff",
							color: "#000",
							border: "1px solid #e0e0e0",
							borderRadius: 8,
							boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
							padding: "16px 24px",
							zIndex: 10001,
							fontFamily: "Inter, sans-serif",
							fontWeight: 600,
							fontSize: 15,
							minWidth: 260,
							textAlign: "center",
							animation: "slideInRight 0.3s ease-out",
						}}>
						A PDF is already being generated.
						<br />
						Please wait for it to finish.
					</div>
				)}
			</Paper>
		</Container>
	);
};

export default UploadPage;
